import {useMemo, useState} from 'react';
import moment from 'moment';
import Paginator from '../Components/Shared/Paginator';
import {FilterValue} from '../Components/Shared/SearchBox';
import {Formats} from '../Constants/Formats';
import {Record} from '../Components/Shared/RecordList';
import {MongoIdSqlId} from '../Types/DtoTypes';
import {SortConfig} from '../Components/Views/Reports';
import {SubmissionStatuses} from '../Constants/SubmissionStatuses';

type HookProps = {
  data: Record[];
  filters: FilterValue;
  search: string;
  pageSize: number;
  pageSizeOptions?: number[];
  statusFilter: string[]; // e.g. ['Draft'], ['Submitted']
  sortConfig?: SortConfig;
};

export function usePaginatedRecords({
  data,
  filters,
  search,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  statusFilter,
  sortConfig,
}: HookProps) {
  const [pageData, setPageData] = useState<Record[]>([]);

  // === Filtered List ===
  const filteredAndSorted = useMemo(() => {
    let result = data.filter(
      r =>
        statusFilter.includes(r.status) &&
        r.name.toLowerCase().includes(search.toLowerCase()),
    );

    if (filters.startDate) {
      const start = moment(filters.startDate, Formats.BACKEND_DATE).toDate();
      result = result.filter(r => r.datetime > start);
    }

    if (filters.endDate) {
      const end = moment(filters.endDate, Formats.BACKEND_DATE).toDate();
      result = result.filter(r => r.datetime < end);
    }

    if (filters.statuses && filters.statuses.length > 0) {
      let filtered: Record[] = [];
      if (filters.statuses?.includes('in-progress'))
        filtered.push(
          ...result.filter(
            r =>
              r.datetime >= moment().toDate() &&
              (r.status === SubmissionStatuses.DRAFT ||
                r.status === SubmissionStatuses.SUBMITTED),
          ),
        );

      if (filters.statuses?.includes('completed'))
        filtered.push(
          ...result.filter(r => r.status === SubmissionStatuses.COMPLETE),
        );

      if (filters.statuses?.includes('overdue'))
        filtered.push(
          ...result.filter(
            r =>
              r.datetime < moment().toDate() &&
              (r.status === SubmissionStatuses.DRAFT ||
                r.status === SubmissionStatuses.SUBMITTED),
          ),
        );

      if (filters.statuses?.includes('dismissed'))
        filtered.push(
          ...result.filter(r => r.status === SubmissionStatuses.DISMISSED),
        );

      result = filtered;
    }

    if (filters.types?.length) {
      result = result.filter(
        r => r.templateId && filters.types?.includes(r.templateId),
      );
    }

    if (filters.investigationAttached || filters.pendingInvestigation) {
      result = result.filter(r => {
        if (filters.investigationAttached && r.hasInvestigation) return true;
        if (
          filters.pendingInvestigation &&
          r.investigationRequired &&
          !r.investigationClosed
        )
          return true;
        return false;
      });
    }

    if (filters.person) {
      let personObj: MongoIdSqlId = JSON.parse(filters.person);
      let personId = personObj.mongoId ?? '';
      result = result.filter(r => r.personId === personId);
    }

    if (filters.firstAidAttendant) {
      let firstAidAttendantObj: MongoIdSqlId = JSON.parse(
        filters.firstAidAttendant,
      );
      let firstAidAttendantId = firstAidAttendantObj.mongoId ?? '';
      result = result.filter(r => r.firstAidAttendant === firstAidAttendantId);
    }

    if (filters.project) {
      let projectObj: MongoIdSqlId = JSON.parse(filters.project);
      let projectId = projectObj.mongoId ?? '';
      result = result.filter(r => r.projectId === projectId);
    }

    if (filters.equipmentType || filters.equipment) {
      let equipmentId = '';
      if (filters.equipment) {
        let equipmentObj: MongoIdSqlId = JSON.parse(filters.equipment);
        equipmentId = equipmentObj.mongoId ?? '';
      }

      result = result.filter(
        r =>
          r.equipmentType === filters.equipmentType &&
          (!equipmentId || r.equipment === equipmentId),
      );
    }
    if (sortConfig) {
      const {key, direction} = sortConfig;
      const dir = direction === 'ascending' ? 1 : -1;
      result.sort((a, b) => {
        try {
          switch (key) {
            case 'date':
              return (a.datetime.getTime() - b.datetime.getTime()) * dir;
            case 'az':
              return (a.name || '').localeCompare(b.name || '') * dir;
            case 'incidentId': {
              const ia = parseInt(a.displayId?.split('_')[1] || '0', 10);
              const ib = parseInt(b.displayId?.split('_')[1] || '0', 10);
              return (ia - ib) * dir;
            }
            default:
              return 0;
          }
        } catch {
          return 0;
        }
      });
    } else {
      result.sort((a, b) => b.datetime.getTime() - a.datetime.getTime());
    }
    return result;
  }, [data, filters, search, statusFilter, sortConfig]);

  // === Paginator Component ===
  const paginator = (
    <Paginator
      data={filteredAndSorted}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      onPaginatedDataChange={setPageData}
      searchDeps={[search, filters]}
    />
  );
  return {
    paginatedData: pageData,
    paginator,
    totalRecords: filteredAndSorted.length,
  };
}
